import React, { useState } from "react";
import api from "../../lib/api";
import { Badge, Empty, Info, Table } from "./_ui";
import {
  ESTABLISHMENT_TYPES,
  INQUIRY_STATUSES,
  STATUS_LABEL,
  customerWhatsAppHref,
  fmtDate,
} from "../../lib/adminUtils";
import { ArrowRightCircle, MessageCircle, Mail, Phone, X, Save, Trash2 } from "lucide-react";

export default function InquiriesView({ items, reload }) {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div data-testid="inquiries-view">
      <div className="flex flex-wrap gap-2 mb-4">
        {["all", ...INQUIRY_STATUSES].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 text-xs rounded font-semibold ${
              filter === k
                ? "bg-slate-950 text-white"
                : "border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
            data-testid={`inq-filter-${k}`}
          >
            {k === "all" ? "Toutes" : STATUS_LABEL[k] || k}{" "}
            <span className="opacity-60 ml-1">
              ({k === "all" ? items.length : items.filter((i) => i.status === k).length})
            </span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Empty label="Aucune demande." />
      ) : (
        <Table
          headers={["Établissement", "Contact", "Ville", "Type", "Surface", "Statut", "Date", ""]}
          testid="inquiries-list"
        >
          {filtered.map((it) => (
            <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="p-3 font-medium text-slate-900">{it.establishment}</td>
              <td className="p-3 text-slate-700">
                {it.contactName}
                <br />
                <span className="text-xs text-slate-500">{it.phone}</span>
              </td>
              <td className="p-3 text-slate-700">{it.city}</td>
              <td className="p-3 text-xs text-slate-600">{it.establishmentType || "—"}</td>
              <td className="p-3 text-slate-700">{it.surface ? `${it.surface} m²` : "—"}</td>
              <td className="p-3">
                <Badge status={it.status} />
              </td>
              <td className="p-3 text-xs text-slate-500">{fmtDate(it.createdAt)}</td>
              <td className="p-3 text-right">
                <button
                  onClick={() => setSelected(it)}
                  className="text-xs text-[#016C36] hover:underline font-semibold"
                  data-testid={`inquiry-open-${it.id}`}
                >
                  Ouvrir
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {selected && (
        <InquiryDrawer
          item={selected}
          onClose={() => setSelected(null)}
          reload={() => {
            reload();
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function InquiryDrawer({ item, onClose, reload }) {
  const [form, setForm] = useState({
    establishment: item.establishment || "",
    contactName: item.contactName || "",
    phone: item.phone || "",
    email: item.email || "",
    address: item.address || "",
    city: item.city || "",
    surface: item.surface || "",
    establishmentType: item.establishmentType || "",
    desiredNeed: item.desiredNeed || item.desiredFormula || "",
    frequencyComment: item.frequencyComment || "",
    message: item.message || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = { ...form, surface: form.surface ? Number(form.surface) : null };
      await api.patch(`/admin/inquiries/${item.id}`, payload);
      reload();
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (s) => {
    await api.patch(`/admin/inquiries/${item.id}`, { status: s });
    reload();
  };

  const convert = async () => {
    if (!window.confirm("Convertir cette demande en client ?")) return;
    const r = await api.post(`/admin/inquiries/${item.id}/convert`);
    reload();
    if (r.data?.customerId) {
      window.dispatchEvent(
        new CustomEvent("safemat:openCustomer", { detail: r.data.customerId })
      );
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement ?")) return;
    await api.delete(`/admin/inquiries/${item.id}`);
    reload();
  };

  const waText = `Bonjour ${item.contactName}, c'est SAFEMAT. Suite à votre demande pour ${item.establishment}, on revient vers vous rapidement.`;

  return (
    <div
      className="fixed inset-0 z-40 bg-slate-950/50 flex justify-end"
      onClick={onClose}
      data-testid="inquiry-drawer"
    >
      <div
        className="w-full max-w-xl bg-white h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-slate-950 text-white px-6 py-4 flex justify-between items-center">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Demande
            </p>
            <h2 className="font-display font-bold text-lg">{item.establishment}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status pills */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Statut
            </p>
            <div className="flex gap-2 flex-wrap">
              {INQUIRY_STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 text-xs rounded font-semibold ${
                    item.status === s
                      ? "bg-slate-950 text-white"
                      : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`inq-status-${s}`}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            {item.phone && (
              <a
                href={`tel:${item.phone}`}
                className="btn-secondary !text-xs"
                data-testid="inq-call"
              >
                <Phone size={12} /> Appeler
              </a>
            )}
            {item.phone && (
              <a
                href={customerWhatsAppHref(item, waText)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#25D366] text-white rounded font-semibold"
                data-testid="inq-wa"
              >
                <MessageCircle size={12} /> WhatsApp
              </a>
            )}
            {item.email && (
              <a
                href={`mailto:${item.email}?subject=SAFEMAT · ${item.establishment}`}
                className="btn-secondary !text-xs"
                data-testid="inq-email"
              >
                <Mail size={12} /> Email
              </a>
            )}
            <button
              onClick={() => setStatus("answered")}
              className="btn-secondary !text-xs"
              data-testid="inq-mark-answered"
            >
              Marquer répondue
            </button>
            <button
              onClick={() => setStatus("lost")}
              className="btn-secondary !text-xs text-slate-500"
              data-testid="inq-mark-lost"
            >
              Perdue
            </button>
          </div>

          {/* Editable fields */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <LabelInput label="Contact" value={form.contactName} onChange={(v) => update("contactName", v)} />
            <LabelInput label="Téléphone" value={form.phone} onChange={(v) => update("phone", v)} />
            <LabelInput label="Email" value={form.email} onChange={(v) => update("email", v)} />
            <LabelInput label="Ville" value={form.city} onChange={(v) => update("city", v)} />
            <div className="col-span-2">
              <LabelInput label="Adresse" value={form.address} onChange={(v) => update("address", v)} />
            </div>
            <LabelInput label="Surface (m²)" type="number" value={form.surface} onChange={(v) => update("surface", v)} />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                Type
              </p>
              <select
                className="field"
                value={form.establishmentType}
                onChange={(e) => update("establishmentType", e.target.value)}
              >
                <option value="">— Type —</option>
                {ESTABLISHMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <LabelInput label="Besoin souhaité" value={form.desiredNeed} onChange={(v) => update("desiredNeed", v)} />
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                Fréquence / commentaire
              </p>
              <textarea
                className="field"
                rows={2}
                value={form.frequencyComment}
                onChange={(e) => update("frequencyComment", e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                Message
              </p>
              <textarea
                className="field"
                rows={3}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
              />
            </div>
            <Info label="Besoin souhaité" value={item.desiredNeed || item.desiredFormula} />
            <Info label="Fréquence / commentaire" value={item.frequencyComment} />
            <Info label="Reçue le" value={fmtDate(item.createdAt)} />
          </div>

          <div className="flex justify-between border-t border-slate-200 pt-4">
            <button
              onClick={remove}
              className="btn-secondary !text-xs text-[#016C36]"
              data-testid="inq-delete"
            >
              <Trash2 size={12} /> Supprimer
            </button>
            <div className="flex gap-2">
              <button onClick={saveAll} disabled={saving} className="btn-secondary !text-xs">
                <Save size={12} /> Enregistrer
              </button>
              {item.status !== "converted" && (
                <button onClick={convert} className="btn-primary !text-xs" data-testid="inq-convert">
                  <ArrowRightCircle size={14} /> Convertir en client
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LabelInput({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
        {label}
      </p>
      <input
        className="field"
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
